import { Box } from '@mui/material';
import { ResponsiveBoxPlot } from '@nivo/boxplot';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EnumSelect } from '~/components/GenericForm/EnumSelect';
import { SolaForceSubheading } from '~/components/SolaForceSubheading';
import { ChartKPI } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/CompensationEqualityCharts/ChartKPI';
import { CompensationBoxPlotTooltip } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/CompensationEqualityCharts/CompensationBoxPlotTooltip';
import { CompensationScatterPlotTooltip } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/CompensationEqualityCharts/CompensationScatterPlotTooltip';
import {
  ScatterTooltipValueConfig,
  useCompensationEqualityCharts,
} from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/CompensationEqualityCharts/useCompensationEqualityCharts';
import {
  chartColors,
  useSxStyles,
} from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/CompensationEqualityCharts/useStyles';
import { CompensationEqualityReportEntry } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/data';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

type PropsType = {
  isLoading: boolean;
  rows: CompensationEqualityReportEntry[];
  currency: string;
};

export const CompensationEqualityCharts = (props: PropsType) => {
  const { isLoading, currency } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const { kpi, charts, scatterXSelector } =
    useCompensationEqualityCharts(props);

  const getGenderColor = useCallback(
    (id: string | number) => {
      switch (id) {
        case 'female':
        case t('laFemale'):
          return chartColors.female;
        case t('laMale'):
        case 'male':
          return chartColors.male;
        default:
          return chartColors.blue;
      }
    },
    [t],
  );

  const { scatterLegend, tooltipLabels, getTooltipValueProps } = useMemo(() => {
    const scatterConfig = scatterXSelector.currentConfig;

    const scatterLegend = {
      yAxis: `${t('laCompensation')} (${currency})`,
      xAxis: scatterConfig?.name ?? t('laUnknown'),
    };

    const tooltipLabels: {
      yAxis: ScatterTooltipValueConfig;
      xAxis: ScatterTooltipValueConfig;
    } = {
      yAxis: {
        label: t('laCompensation'),
        valueQualifier: currency,
      },
      xAxis: scatterConfig?.tooltip ?? {
        label: t('laUnknown'),
      },
    };

    const getTooltipValueProps =
      (config: ScatterTooltipValueConfig) =>
      (value?: string | number): { label: string; value?: string | number } => {
        const { label, valueQualifier } = config;

        return {
          label,
          value: valueQualifier ? `${value} ${valueQualifier}` : value,
        };
      };

    return {
      scatterLegend,
      tooltipLabels,
      getTooltipValueProps,
    };
  }, [t, currency, scatterXSelector]);

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box>
      <Box sx={sxStyles.kpiLayout}>
        <Box sx={{ gridArea: 'genderLabel' }}>{t('laGender')}</Box>
        <Box sx={{ gridArea: 'ageLabel' }}>{t('averageAge')}</Box>
        <Box sx={{ gridArea: 'compensationLabel' }}>
          {t('averageCompensation')}
        </Box>
        <Box sx={{ gridArea: 'femalesAllLabel' }}>
          {t('percentageOfFemalesInAll')}
        </Box>
        <Box sx={{ gridArea: 'femalesMalesLabel' }}>
          {t('percentageOfFemalesToMales')}
        </Box>
        <ChartKPI
          value={`${kpi.female.employeeCount}`}
          label={t('laFemale')}
          sx={{ gridArea: 'femaleCount', ...sxStyles.femaleKpi }}
        />
        <ChartKPI
          value={`${kpi.female.averageAge} ${t('laYears')}`}
          label={t('laFemale')}
          sx={{ gridArea: 'femaleAge', ...sxStyles.femaleKpi }}
        />
        <ChartKPI
          value={`${Math.round(kpi.female.averageCompensation)} ${currency}`}
          label={t('laFemale')}
          sx={{ gridArea: 'femaleCompensation', ...sxStyles.femaleKpi }}
        />
        <ChartKPI
          value={`${kpi.male.employeeCount}`}
          label={t('laMale')}
          sx={{ gridArea: 'maleCount', ...sxStyles.maleKpi }}
        />
        <ChartKPI
          value={`${kpi.male.averageAge} ${t('laYears')}`}
          label={t('laMale')}
          sx={{ gridArea: 'maleAge', ...sxStyles.maleKpi }}
        />
        <ChartKPI
          value={`${Math.round(kpi.male.averageCompensation)} ${currency}`}
          label={t('laMale')}
          sx={{ gridArea: 'maleCompensation', ...sxStyles.maleKpi }}
        />
        <ChartKPI
          value={
            kpi.femalesPercentageOfAll.isValid
              ? `${kpi.femalesPercentageOfAll.percentage.toFixed(1)} %`
              : 'N/A'
          }
          sx={{ gridArea: 'femalesAll', ...sxStyles.blueKpi }}
        />
        <ChartKPI
          value={
            kpi.femalesPercentageToMales.isValid
              ? `${kpi.femalesPercentageToMales.percentage.toFixed(1)} %`
              : 'N/A'
          }
          sx={{ gridArea: 'femalesMales', ...sxStyles.darkBlueKpi }}
        />
      </Box>
      <Box sx={sxStyles.chartHeading}>
        <Box sx={sxStyles.chartHeadingWithSelector}>
          <SolaForceSubheading>
            {t('compensationByGenderInRelationTo')}
          </SolaForceSubheading>
          <EnumSelect
            enumOptions={scatterXSelector.options}
            value={scatterXSelector.value}
            onChange={(event) => {
              const value = event.target.value;
              if (!!value && typeof value === 'string') {
                scatterXSelector.onChange(value);
              }
            }}
            size={'small'}
          />
        </Box>
      </Box>
      <Box sx={sxStyles.chartsLayout}>
        <Box sx={sxStyles.chartContainer}>
          <ResponsiveScatterPlot
            {...charts.scatterPlot}
            margin={{ top: 10, right: 140, bottom: 70, left: 90 }}
            colors={(node) => getGenderColor(node.serieId)}
            axisBottom={{ legend: scatterLegend.xAxis, legendOffset: 46 }}
            axisLeft={{
              legend: scatterLegend.yAxis,
              legendOffset: -60,
            }}
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'column',
                translateX: 130,
                itemWidth: 100,
                itemHeight: 16,
                itemsSpacing: 3,
                symbolShape: 'circle',
              },
            ]}
            tooltip={(tooltipProps) => (
              <CompensationScatterPlotTooltip
                {...tooltipProps}
                axisProps={{
                  getXProps: getTooltipValueProps(tooltipLabels.xAxis),
                  getYProps: getTooltipValueProps(tooltipLabels.yAxis),
                }}
              />
            )}
          />
        </Box>
        <Box sx={sxStyles.chartContainer}>
          <ResponsiveBoxPlot
            {...charts.boxPlot}
            margin={{ top: 10, right: 140, bottom: 70, left: 90 }}
            colorBy={'group'}
            colors={(node) => getGenderColor(node.group)}
            axisBottom={{ legend: t('laGender'), legendOffset: 46 }}
            axisLeft={{ legend: scatterLegend.yAxis, legendOffset: -60 }}
            borderRadius={2}
            medianColor={{ from: 'color', modifiers: [['darker', 3]] }}
            whiskerColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'column',
                translateX: 130,
                itemWidth: 100,
                itemHeight: 16,
                itemsSpacing: 3,
                symbolShape: 'circle',
              },
            ]}
            tooltip={(tooltipProps) => (
              <CompensationBoxPlotTooltip
                {...tooltipProps}
                currency={currency}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};
